import MarkdownIt from 'markdown-it';
import StateBlock from 'markdown-it/lib/rules_block/state_block.mjs';

const CustomTags = [
  'slack-reference-node',
  'google-drive-reference-node',
  'linear-reference-node',
  'notion-reference-node',
  'footnote-list-node',
  'hidden-ai-node',
];
const TagPattern = CustomTags.join('|');
const CustomTagRegex = new RegExp(`^<(${TagPattern})[^>]*>`, 'i');
const OpeningTagRegex = new RegExp(`^\\s*<(${TagPattern})(?:\\s[^>]*)?>`, 'i');
const ClosingTagRegex = new RegExp(`</(${TagPattern})>`, 'i');
const CustomTagAttributesRegex = new RegExp(
  `(<(?:${TagPattern})[^>]*?content=")([^"]*)("[^>]*?>)`,
  'g',
);

/**
 * Ensure reference nodes are on their own separate line
 *
 * By default CommonMark spec treats unknown HTML tags as inline HTML, unless they're separated from
 * surrounding content by blank lines (this terminates the paragraph they're in). A single newline in a paragraph
 * is not enough to break the reference into its own block.
 */
export const ensureReferenceBlocks = (md: string) =>
  md
    .split('\n')
    .map((line) => (line.match(CustomTagRegex) ? `\n${line}` : line))
    .join('\n');

/**
 * Ensure footnote nodes that appear on the same line as block-level elements (like code blocks)
 * are properly separated with a blank line.
 * Fixes issues where AI-generated content places <footnote-node> tags immediately after
 * closing code fences (```<footnote-node...>), which will cause the closing mark to be parsed incorrectly by Tiptap.
 */
export const ensureFootnoteFormatting = (md: string) => {
  const lines = md.split('\n');
  const result: string[] = [];

  for (const line of lines) {
    const codeBlockEndMatch = line.match(/^(\s*```)(<footnote-node.*)$/);
    if (codeBlockEndMatch) {
      result.push(codeBlockEndMatch[1]);
      result.push('');
      result.push(codeBlockEndMatch[2]);
      continue;
    }

    result.push(line);
  }

  return result.join('\n');
};

/**
 * Ensure hidden-ai-node tags are on their own lines with blank lines before and after.
 * TTQ often places these inline with text or after <br> tags, which causes
 * them to be treated as inline elements instead of block-level, leading to them being lost
 * when the editor content is saved.
 * Example:
 * text<hidden-ai-node>... -> text\n\n<hidden-ai-node>...\n\n
 */
export const ensureHiddenAINodeFormatting = (md: string) => {
  let result = md.replace(/(<hidden-ai-node[^>]*>.*?<\/hidden-ai-node>)/gs, '\n\n$1\n\n');
  result = result.replace(/\n{3,}/g, '\n\n');
  return result.trim();
};

export function htmlCustomTags(md: MarkdownIt) {
  function referenceNodeBlockRule(
    state: StateBlock,
    startLine: number,
    endLine: number,
    silent: boolean,
  ) {
    const pos = state.bMarks[startLine] + state.tShift[startLine];
    const max = state.eMarks[startLine];

    const lineText = state.src.slice(pos, max);
    const matches = lineText.match(OpeningTagRegex);
    if (matches === null) {
      return false;
    }

    if (silent) {
      return true;
    }

    let currentLine = startLine;
    let foundClosingTag = false;
    const tagName = matches[1].toLowerCase();
    const rawContentLines = [];

    while (currentLine < endLine) {
      const currentLinePos = state.bMarks[currentLine] + state.tShift[currentLine];
      const currentLineMax = state.eMarks[currentLine];
      const currentLineText = state.src.slice(currentLinePos, currentLineMax);

      rawContentLines.push(currentLineText);

      if (ClosingTagRegex.test(currentLineText)) {
        foundClosingTag = true;
        break;
      }
      currentLine++;
    }

    if (!foundClosingTag) {
      return false;
    }

    state.line = currentLine + 1; // Indicate to markdown-it this line has been consumed
    const token = state.push(`${tagName}_block`, tagName, 0);
    token.block = true;
    token.map = [startLine, state.line];
    token.markup = '';
    token.content = rawContentLines.join('\n');
    return true;
  }

  md.block.ruler.before('html_block', 'custom_html_tags', referenceNodeBlockRule, {
    alt: ['paragraph', 'html_block'],
  });

  CustomTags.forEach((tag) => {
    const rendererName = `${tag.toLowerCase()}_block`;
    md.renderer.rules[rendererName] = (tokens, idx) => tokens[idx].content + '\n';
  });
}

function escapeAttributeValue(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    .replace(/\n/g, '&#xA;')
    .replace(/\r/g, '&#xD;');
}

export function escapeAttributes(htmlString: string) {
  return htmlString.replace(CustomTagAttributesRegex, (_match, prefix, content, suffix) => {
    const escapedContent = escapeAttributeValue(content);
    return prefix + escapedContent + suffix;
  });
}
